/* eslint-disable camelcase */
// @flow
import { push, goBack } from 'connected-react-router';
import { omit } from 'lodash';
import moment from 'moment';
import { makeActionCreator, reduce } from '../../helpers/utils';
import ApiClient from '../../api/Api';
import { notify } from '../../helpers/BugsnagClient';
import type { StoreState } from '../reducer';
import type { CheckoutResponse } from '../../types';
import { roundDateToNearest } from '../../helpers/utils';

const debug = require('debug')('jp:reducers:checkout');

export const INIT_CHECKOUT = 'checkout/INIT_CHECKOUT';
export const LOAD_LISTING = 'checkout/LOAD_LISTING';
export const LOAD_LISTING_SUCCESS = 'checkout/LOAD_LISTING_SUCCESS';
export const LOAD_LISTING_FAIL = 'checkout/LOAD_LISTING_FAIL';
export const LOAD_CHECKSUM = 'checkout/LOAD_CHECKSUM';
export const LOAD_CHECKSUM_SUCCESS = 'checkout/LOAD_CHECKSUM_SUCCESS';
export const LOAD_CHECKSUM_FAIL = 'checkout/LOAD_CHECKSUM_FAIL';
export const CREATE_BOOKING = 'checkout/CREATE_BOOKING';
export const CREATE_BOOKING_SUCCESS = 'checkout/CREATE_BOOKING_SUCCESS';
export const CREATE_BOOKING_FAIL = 'checkout/CREATE_BOOKING_FAIL';
export const SET_DATES = 'checkout/SET_DATES';
export const CLEAR_GLOBAL_ERROR = 'checkout/CLEAR_GLOBAL_ERROR';
export const CREATE_PHONE_NUMBER = 'checkout/CREATE_PHONE_NUMBER';
export const CREATE_PHONE_NUMBER_SUCCESS =
  'checkout/CREATE_PHONE_NUMBER_SUCCESS';
export const CREATE_PHONE_NUMBER_FAIL = 'checkout/CREATE_PHONE_NUMBER_FAIL';
export const SET_PERSONAL_DETAILS_LOCK = 'checkout/SET_PERSONAL_DETAILS_LOCK';
export const SET_PERSONAL_DETAILS_AUTOLOCK =
  'checkout/SET_PERSONAL_DETAILS_AUTOLOCK';
export const ADD_PROMO_CODE = 'checkout/ADD_PROMO_CODE';
export const ADD_PROMO_CODE_SUCCESS = 'checkout/ADD_PROMO_CODE_SUCCESS';
export const ADD_PROMO_CODE_FAIL = 'checkout/ADD_PROMO_CODE_FAIL';
export const SET_SHOW_REGISTRATION_FORM = 'checkout/SET_SHOW_REGISTRATION_FORM';
export const SET_SHOW_DRIVEUP_MODAL = 'checkout/SET_SHOW_DRIVEUP_MODAL';

export type QueryParams = {|
  endDate: string,
  endTime: string,
  startDate: string,
  startTime: string
|};

export type PaymentMethod = {|
  id: number,
  source: string,
  authority: ?string,
  isPrimary: boolean,
  isBlocked: boolean,
  isAuthorised: boolean,
  createdAt: string,
  name: string,
  cardType: string,
  lastFour: ?string,
  expiryDate: string,
  email?: string,
  isActive: boolean
|};

type v5ErrorResponse = {|
  error: number,
  message: string,
  meta: mixed[]
|};

type Action =
  | {| type: typeof INIT_CHECKOUT, payload: QueryParams |}
  | {| type: typeof LOAD_LISTING |}
  | {| type: typeof LOAD_LISTING_SUCCESS, payload: Object |}
  | {| type: typeof LOAD_LISTING_FAIL |}
  | {| type: typeof LOAD_CHECKSUM |}
  | {| type: typeof LOAD_CHECKSUM_SUCCESS, payload: Object |}
  | {| type: typeof LOAD_CHECKSUM_FAIL, payload: v5ErrorResponse |}
  | {| type: typeof SELECT_VEHICLE, id: number |}
  | {| type: typeof CREATE_BOOKING, listingId: number |}
  | {| type: typeof SET_PERSONAL_DETAILS_LOCK, lock: boolean |}
  | {| type: typeof SET_PERSONAL_DETAILS_AUTOLOCK, lock: boolean |}
  | {| type: typeof CLEAR_GLOBAL_ERROR |}
  | {|
      type: typeof CREATE_BOOKING_SUCCESS,
      listingId: number,
      payload: Object
    |}
  | {| type: typeof CREATE_BOOKING_FAIL, payload: Object |}
  | {| type: typeof ADD_PROMO_CODE |}
  | {| type: typeof ADD_PROMO_CODE_SUCCESS |}
  | {| type: typeof ADD_PROMO_CODE_FAIL |}
  | {| type: typeof SET_SHOW_REGISTRATION_FORM, bool: boolean |}
  | {| type: typeof SET_SHOW_DRIVEUP_MODAL, value: boolean |}
  | {|
      types: Array<string>,
      params?: Object,
      promise: ApiClient => Promise<{}>
    |};

export type State = {|
  loaded: boolean,
  loadingListing: boolean,
  listingLoaded: boolean,
  listing: {
    excludedPaymentMethods: []
  },
  vehiclesLoaded: boolean,
  loadingVehicleLookup: boolean,
  startDate: ?moment,
  endDate: ?moment,
  checkoutSummaryLoaded: boolean,
  checkoutSummary: ?Object,
  loadingCheckoutSummary: boolean,
  displayAllPaymentMethods: boolean,
  userAddingPaymentMethod: boolean,
  apiErrors: {
    availability: ?string
  },
  checkingOut: boolean,
  bookingResponse: ?CheckoutResponse,
  globalApiError: ?{|
    title: string,
    code: number,
    identifier: string
  |},
  personalDetailsLocked: boolean,
  completedPersonalDetailsOnce: boolean,
  addPhoneNumberLoading: boolean,
  personalDetailsAutoLockComplete: boolean,
  submittingPromo: boolean,
  promoError: ?string,
  promoAdded: boolean,
  showRegistrationForm: boolean,
  showDriveupModal: boolean
|};

export const initialState: State = {
  loaded: false,
  loadingListing: false,
  listingLoaded: false,
  listing: {
    excludedPaymentMethods: []
  },
  vehiclesLoaded: false,
  loadingVehicleLookup: false,
  startDate: null,
  endDate: null,
  checkoutSummaryLoaded: false,
  checkoutSummary: null,
  loadingCheckoutSummary: false,
  apiErrors: {
    availability: null
  },
  checkingOut: false,
  bookingResponse: null,
  globalApiError: null,
  personalDetailsLocked: false,
  completedPersonalDetailsOnce: false,
  addPhoneNumberLoading: false,
  personalDetailsAutoLockComplete: false,
  submittingPromo: false,
  promoError: null,
  promoAdded: false,
  showRegistrationForm: false,
  showDriveupModal: false
};

export default reduce(initialState, {
  [INIT_CHECKOUT](state: State, action: Action): State {
    debug(INIT_CHECKOUT, action);

    if (
      action &&
      action.payload &&
      typeof action.payload.duration === 'string' &&
      action.payload.duration.includes('-')
    ) {
      // If the user has come from a marketing link the start date is now + 24 hours
      const roundedStartDate = roundDateToNearest(moment(), 5).add(1, 'day');

      const duration = action.payload.duration.split('-');
      const endDate = roundedStartDate
        .clone()
        .add(parseInt(duration[0], 10), duration[1]);

      return {
        ...state,
        startDate: roundedStartDate,
        endDate
      };
    }
    if (
      action &&
      action.payload &&
      action.payload.startDate &&
      action.payload.endDate
    ) {
      const startDate = moment(
        `${action.payload.startDate} ${action.payload.startTime}`,
        'YYYY-MM-DD HH:mm'
      ).startOf('minute');
      const endDate = moment(
        `${action.payload.endDate} ${action.payload.endTime}`,
        'YYYY-MM-DD HH:mm'
      ).startOf('minute');

      if (endDate.isSameOrBefore(startDate)) {
        return {
          ...state,
          startDate,
          endDate: startDate.clone().add(2, 'hours')
        };
      }

      return { ...state, startDate, endDate };
    }
    const roundedStartDate = roundDateToNearest(moment(), 5);
    return {
      ...state,
      startDate: roundedStartDate,
      endDate: roundedStartDate.clone().add(2, 'hours')
    };
  },
  [LOAD_CHECKSUM](state: State): State {
    debug(LOAD_CHECKSUM);
    return { ...state, loadingCheckoutSummary: true };
  },
  [LOAD_CHECKSUM_SUCCESS](state: State, action: Action): State {
    debug(LOAD_CHECKSUM_SUCCESS, action);
    if (action.payload && action.payload.data) {
      const checkoutSummary = action.payload.data;
      const promoAdded = checkoutSummary.price
        ? checkoutSummary.price.breakdown.filter(
            item => item.key === 'discount'
          ).length > 0
        : false;
      return {
        ...state,
        loadingCheckoutSummary: false,
        checkoutSummaryLoaded: !!checkoutSummary,
        checkoutSummary: { ...checkoutSummary },
        promoAdded,
        apiErrors: {
          ...state.apiErrors,
          availability: null
        }
      };
    }
    throw new Error('No checkout summary in checksum response');
  },
  [LOAD_CHECKSUM_FAIL](state: State, action: Action): State {
    debug(LOAD_CHECKSUM_FAIL, action);
    if (action && action.payload) {
      if (action.payload.error && action.payload.message) {
        // availability error
        return {
          ...state,
          checkoutSummaryLoaded: false,
          loadingCheckoutSummary: false,
          checkoutSummary: null,
          apiErrors: {
            ...state.apiErrors,
            availability:
              typeof action.payload.message === 'string'
                ? action.payload.message
                : null
          }
        };
      }
    }
    return {
      ...state,
      checkoutSummaryLoaded: false,
      loadingCheckoutSummary: false,
      checkoutSummary: null
    };
  },
  [LOAD_LISTING](state: State): State {
    debug(LOAD_LISTING);
    return { ...state, loadingListing: true };
  },
  [LOAD_LISTING_SUCCESS](state: State, action: Action): State {
    debug(LOAD_LISTING_SUCCESS, action);
    if (action.payload && action.payload.data) {
      const listing = action.payload.data;

      return {
        ...state,
        loadingListing: false,
        listingLoaded: !!listing,
        listing: { ...listing },
        showDriveupModal: listing.acceptsDriveup
      };
    }
    throw new Error('No listing in checkout listing details response');
  },
  [LOAD_LISTING_FAIL](state: State): State {
    debug(LOAD_LISTING_FAIL);
    return { ...state, loadingListing: false, listingLoaded: false };
  },
  [SET_SHOW_DRIVEUP_MODAL](state: State, action: Action): State {
    debug(LOAD_LISTING_FAIL);
    return { ...state, showDriveupModal: action.value };
  },
  [CREATE_BOOKING](state: State): State {
    debug(CREATE_BOOKING);
    return { ...state, checkingOut: true, globalApiError: null };
  },
  [CREATE_BOOKING_SUCCESS](state: State, action: Action): State {
    debug(CREATE_BOOKING_SUCCESS, action);
    if (action.payload && action.payload.data) {
      return {
        ...state,
        checkingOut: false,
        bookingResponse: action.payload.data
      };
    }

    notify(new Error('No data in create booking success'), {
      metaData: action
    });

    return {
      ...state,
      checkingOut: false
    };
  },
  [CREATE_BOOKING_FAIL](state: State, action: Action): State {
    debug(CREATE_BOOKING_FAIL, action);
    // if a generic failure that we can't attribute to any relevant field to ours (paymentMethodId, vehicleId, startTime, endTime)
    // add a global error to checkout store with this error, else, add to apiErrors for validation engine
    if (action.payload && action.payload.data) {
      const { code, all } = action.payload.data;
      if (code >= 100001 && code <= 199999 && all) {
        // then this is a validation error coming off of v4 book
        const apiErrors = Object.keys(all).reduce((acc, cur) => {
          if (cur === 'paymentId') {
            acc.paymentMethodId = all[cur].title;
          } else {
            acc[cur] = all[cur].title;
          }
          return acc;
        }, {});

        return {
          ...state,
          checkingOut: false,
          apiErrors: {
            ...state.apiErrors,
            ...apiErrors
          }
        };
      }
      // then this is an error associated with the entire booking process so we should modal it
      return {
        ...state,
        checkingOut: false,
        globalApiError: action.payload.data
      };
    }

    notify(new Error('Booking fail. No payload in data'), {
      metaData: action
    });

    return {
      ...state,
      checkingOut: false
    };
  },
  [ADD_PROMO_CODE](state: State) {
    debug(ADD_PROMO_CODE);
    return {
      ...state,
      submittingPromo: true
    };
  },
  [ADD_PROMO_CODE_FAIL](state: State, action: Action): State {
    debug(ADD_PROMO_CODE_FAIL);
    if (action.payload && action.payload.data) {
      return {
        ...state,
        submittingPromo: false,
        promoError: action.payload.data.title,
        promoAdded: false
      };
    }
    return {
      ...state,
      submittingPromo: false,
      promoAdded: false,
      promoError: 'Code could not be applied'
    };
  },
  [ADD_PROMO_CODE_SUCCESS](state: State): State {
    debug(ADD_PROMO_CODE_SUCCESS);
    return {
      ...state,
      promoAdded: true,
      submittingPromo: false
    };
  },
  [SET_DATES](state: State, action: Action): State {
    debug(SET_DATES, action);
    const { leaving, arriving } = action.dates[0];

    return {
      ...state,
      startDate: arriving,
      endDate: leaving
    };
  },
  [CLEAR_GLOBAL_ERROR](state: State): State {
    debug(CLEAR_GLOBAL_ERROR);
    return {
      ...state,
      globalApiError: null
    };
  },
  [CREATE_PHONE_NUMBER](state: State): State {
    debug(CREATE_PHONE_NUMBER);
    // clear our any apiError we previously received from the api
    const apiErrors = omit(state.apiErrors, ['phoneNumber']);
    return { ...state, apiErrors, addPhoneNumberLoading: true };
  },
  [CREATE_PHONE_NUMBER_FAIL](state: State, action: Action): State {
    debug(CREATE_PHONE_NUMBER_FAIL);

    if (action.payload && action.payload.data) {
      return {
        ...state,
        addPhoneNumberLoading: false,
        apiErrors: {
          ...state.apiErrors,
          phoneNumber: [action.payload.data.title]
        }
      };
    }
    return {
      ...state,
      addPhoneNumberLoading: false
    };
  },
  [CREATE_PHONE_NUMBER_SUCCESS](state: State): State {
    debug(CREATE_PHONE_NUMBER_SUCCESS);

    return {
      ...state,
      addPhoneNumberLoading: false,
      personalDetailsLocked: true,
      completedPersonalDetailsOnce: true
    };
  },
  [SET_PERSONAL_DETAILS_LOCK](state: State, action: Action): State {
    debug(SET_PERSONAL_DETAILS_LOCK);

    if (typeof action.lock === 'boolean') {
      return {
        ...state,
        personalDetailsLocked: action.lock,
        completedPersonalDetailsOnce:
          action.lock === true ? true : state.completedPersonalDetailsOnce
      };
    }

    return {
      ...state
    };
  },

  [SET_PERSONAL_DETAILS_AUTOLOCK](state: State, action: Action): State {
    debug(SET_PERSONAL_DETAILS_AUTOLOCK);

    if (typeof action.lock === 'boolean') {
      return {
        ...state,
        personalDetailsLocked: action.lock,
        completedPersonalDetailsOnce: action.lock,
        personalDetailsAutoLockComplete: true
      };
    }

    return {
      ...state,
      personalDetailsLocked: false,
      personalDetailsAutoLockComplete: true
    };
  },
  [SET_SHOW_REGISTRATION_FORM](state: State, action: Action): State {
    return {
      ...state,
      showRegistrationForm: action.bool
    };
  }
});

type CheckoutSummaryCall = {
  listingId: number,
  startTime: string,
  endTime: string,
  vehicleId: ?number,
  paymentId: ?number
};

export function postCheckoutSummary(summary: CheckoutSummaryCall) {
  return {
    types: [LOAD_CHECKSUM, LOAD_CHECKSUM_SUCCESS, LOAD_CHECKSUM_FAIL],
    promise: (client: ApiClient) => client.checkout.summary(summary)
  };
}

export function loadListing(listingId: number) {
  return {
    types: [LOAD_LISTING, LOAD_LISTING_SUCCESS, LOAD_LISTING_FAIL],
    promise: (client: ApiClient) =>
      client.locationDetails.listingDetails(listingId)
  };
}

export function addUserPhoneNumber(model: Object, delay: Number) {
  return (dispatch: (*) => Promise<*>, getState: () => StoreState) => {
    const { countryCode, phoneNumber } = model;
    const {
      auth: {
        user: { phone }
      }
    } = getState();

    return new Promise((resolve, reject) => {
      const phoneNumberStr = `+${countryCode}${phoneNumber.replace(/ /g, '')}`;

      if (phone === phoneNumberStr) {
        // Number already added to users account
        return resolve();
      }

      return dispatch({
        types: [
          CREATE_PHONE_NUMBER,
          CREATE_PHONE_NUMBER_SUCCESS,
          CREATE_PHONE_NUMBER_FAIL
        ],
        promise: (client: ApiClient) =>
          client.phoneNumbers
            .create(countryCode, phoneNumber.replace(/ /g, ''), delay)
            .then(data => {
              resolve();
              return data;
            })
            .catch(err => {
              reject(err);
              throw err;
            }),
        countryCode,
        phoneNumber
      });
    });
  };
}

const createBooking = (dispatch, bookingData) =>
  new Promise((resolve, reject) => {
    dispatch({
      types: [CREATE_BOOKING, CREATE_BOOKING_SUCCESS, CREATE_BOOKING_FAIL],
      promise: (client: ApiClient) =>
        client.checkout
          .book(bookingData.listingId, {
            ...bookingData,
            // TODO: Update the model key to be paymentId so we don't have this map here
            paymentId: bookingData.paymentMethodId,
            startDate: bookingData.startDate,
            endDate: bookingData.endDate
          })
          .then(booking => {
            resolve(booking);
            return booking;
          })
          .catch(err => {
            reject(err);
            throw err;
          }),
      listingId: bookingData.listingId
    });
  });

export function doBooking(model: Object) {
  return (dispatch: (*) => Promise<*>) =>
    createBooking(dispatch, {
      ...model
    }).catch(err => {
      // Errors handled elsewhere
      console.error(err);
    });
}

export function addPromoCode(nonce: string) {
  return {
    types: [ADD_PROMO_CODE, ADD_PROMO_CODE_SUCCESS, ADD_PROMO_CODE_FAIL],
    promise: (client: ApiClient) => client.checkout.addPromo(nonce)
  };
}

export const initCheckout = makeActionCreator(INIT_CHECKOUT, 'payload');
export const setDates = makeActionCreator(SET_DATES, 'dates');

export const acknowledgeGlobalApiError = makeActionCreator(CLEAR_GLOBAL_ERROR);

export const setPersonalDetailsLock = makeActionCreator(
  SET_PERSONAL_DETAILS_LOCK,
  'lock'
);

export const setPersonalDetailsAutoLock = makeActionCreator(
  SET_PERSONAL_DETAILS_AUTOLOCK,
  'lock'
);

export const setShowRegistrationForm = makeActionCreator(
  SET_SHOW_REGISTRATION_FORM,
  'bool'
);

export const setShowDriveupModal = makeActionCreator(
  SET_SHOW_DRIVEUP_MODAL,
  'value'
);

export function cancel() {
  return push('/');
}

export function change() {
  return goBack();
}

export const analyticsEventSubscribers = {
  [CREATE_BOOKING_SUCCESS]: (action: Action, analytics: Analytics) => {
    if (!(action.payload && action.payload.data)) {
      return;
    }

    const { driveUp } = action.payload.data;

    analytics.track('action:payment:successful', {
      type: {
        driveUpPay: driveUp,
        preBook: !driveUp
      }
    });
  },

  [CREATE_BOOKING_FAIL]: (action: Action, analytics: Analytics) => {
    analytics.track('action:payment:failed');
  }
};
